/**
 * Navigation Routes Configuration
 * Defines all routes with role-based access control
 */

import { LucideIcon } from 'lucide-react'
import {
  Home,
  Users,
  FileText,
  Calendar,
  Briefcase,
  Settings,
  BarChart3,
  UserCog,
  Building2,
  Scale,
} from 'lucide-react'

export interface NavigationItem {
  id: string
  label: string
  path: string
  icon: LucideIcon
  roles?: string[] // If undefined, accessible to all authenticated users
  children?: NavigationItem[]
}

/**
 * Navigation configuration with role-based access
 * Matches legacy Flask role restrictions:
 * - admin (role_id=1): Full access
 * - lawyer (role_id=2): Visits, Reports, Cases, Clients
 * - staff (role_id=3): Cases, Clients only
 */
export const navigationConfig: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: Home,
    // All authenticated users
  },
  {
    id: 'clients',
    label: 'Clients',
    path: '/clients',
    icon: Users,
    // All authenticated users (admin, lawyer, staff)
  },
  {
    id: 'visits',
    label: 'Visits',
    path: '/visits',
    icon: Calendar,
    roles: ['admin', 'lawyer'], // Admin & Lawyer only (like legacy @role_required(1, 2))
  },
  {
    id: 'cases',
    label: 'Cases',
    path: '/cases',
    icon: Briefcase,
    // All authenticated users (admin, lawyer, staff)
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: BarChart3,
    roles: ['admin', 'lawyer'], // Admin & Lawyer only (like legacy @role_required(1, 2))
  },
  {
    id: 'admin',
    label: 'Admin Settings',
    path: '/admin',
    icon: Settings,
    roles: ['admin'], // Admin only (like legacy @role_required(1))
    children: [
      {
        id: 'admin-users',
        label: 'User Management',
        path: '/admin/users',
        icon: UserCog,
      },
      {
        id: 'admin-offices',
        label: 'Office Management',
        path: '/admin/offices',
        icon: Building2,
      },
      {
        id: 'admin-courts',
        label: 'Court Management',
        path: '/admin/courts',
        icon: Scale,
      },
    ],
  },
]

/**
 * Filter navigation items based on user roles
 */
export function filterNavigationByRoles(
  items: NavigationItem[],
  userRoles: string[]
): NavigationItem[] {
  return items
    .filter((item) => {
      // If no roles specified, item is accessible to all authenticated users
      if (!item.roles || item.roles.length === 0) return true

      // Check if user has any of the required roles
      return item.roles.some((role) => userRoles.includes(role))
    })
    .map((item) => ({
      ...item,
      // Recursively filter children
      children: item.children
        ? filterNavigationByRoles(item.children, userRoles)
        : undefined,
    }))
}
